import { defaultCacheableSingleton } from '@/lib/cacheable';
import { hygraphClient } from '@/lib/graphql-clients';
import { DisciplineType, getSdk, Maybe } from '@/lib/types';
import { Discipline, Specialty } from '@/lib/types';

import NomadApiClient from '@/pages/api/api-clients/nomad-api';
import { isNurse } from '@/utils/discipline-utils';

export type NomadApiSpecialty = {
  id: string;
  name: string;
  discipline: Discipline;
};

const getAll = async () => {
  const sdk = getSdk(hygraphClient());
  const { specialties } = await sdk.Specialties();
  return specialties as Specialty[];
};

const getByDiscipline = async (discipline: Maybe<Discipline> | undefined) => {
  return defaultCacheableSingleton.remember(
    `specialties-${discipline?.id}`,
    async () => {
      const sdk = getSdk(hygraphClient());
      const { specialties } = await sdk.Specialties({
        where: { discipline },
      });

      return specialties as Specialty[];
    }
  );
};

const getByDisciplineType = async (
  disciplineType: Maybe<DisciplineType> | undefined
) => {
  return defaultCacheableSingleton.remember(
    `specialties-${disciplineType}`,
    async () => {
      const sdk = getSdk(hygraphClient());
      const { specialties } = await sdk.Specialties({
        where: { discipline: { disciplineType } },
      });

      return specialties as Specialty[];
    }
  );
};

const getNomadSpecialtiesByDiscipline = async (discipline: Discipline) => {
  const urn = isNurse(discipline.disciplineType)
    ? 'specializations/rn'
    : 'specializations/provider';
  const { data } = await NomadApiClient.get<NomadApiSpecialty[]>(urn, {
    params: {
      discipline: discipline.apiId,
    },
    transformResponse: (data) => {
      return JSON.parse(data)['data'].map(
        (
          specialty: Omit<NomadApiSpecialty, 'discipline'>
        ): NomadApiSpecialty => ({
          ...specialty,
          discipline,
        })
      );
    },
  });

  return data;
};

const SpecialtiesService = {
  getAll,
  getByDiscipline,
  getByDisciplineType,
  getNomadSpecialtiesByDiscipline,
};

export default SpecialtiesService;
